import type Highcharts from 'highcharts';
import type { ChartRenderCallbackFunction } from 'highcharts/highstock';
import { formatNumberToCompactNotation } from '../../../utils/renderUtils';

export const chartAssetColors = [
    '#363C5F', // color-chart-royal-blue-90
    '#656C90', // color-chart-royal-blue-70
    '#A9ADC3', // color-chart-royal-blue-50
    '#CBCDDC', // color-chart-royal-blue-30
    '#EDEEF5', // color-chart-royal-blue-10
    '#282D47', // color-chart-royal-blue-100
    '#434D77', // color-chart-royal-blue-80
    '#878CA9', // color-chart-royal-blue-60
    '#BABDCF', // color-chart-royal-blue-40
    '#DCDEE8', // color-chart-royal-blue-20
];
export const chartLiabilityColors = [
    '#5B5845', // color-chart-stone-90
    '#9E9A7C', // color-chart-stone-70
    '#CDC9AD', // color-chart-stone-50
    '#E6E4D6', // color-chart-stone-30
    '#F9F8F5', // color-chart-stone-10
    '#4A4837', // color-chart-stone-100
    '#7D7961', // color-chart-stone-80
    '#C0BB98', // color-chart-stone-60
    '#D9D6CA', // color-chart-stone-40
    '#F2F1EA', // color-chart-stone-20
];

export type LegendPosition = 'left' | 'right';

interface CustomPointOptions extends Highcharts.PointOptionsObject {
    weight: number;
}

interface IChartTitleOptions {
    title: string;
    amount: number;
    currency: string;
}

const horizontallyCenterTitle = (chart: Highcharts.Chart) => {
    const title = chart.title;
    title.attr({
        x: chart.plotLeft + chart.plotWidth / 2,
    });
};

interface IPieChartParams {
    width?: number;
    titleOptions: IChartTitleOptions;
    seriesData?: (number | [string, number] | Highcharts.PointOptionsObject)[];
    legend: {
        width: number;
        floatLegend: boolean;
        legendPosition: LegendPosition;
        title: {
            text: string;
        };
        legendRtl?: boolean;
    };
}

export const getPieChartOptions = (params: IPieChartParams): Highcharts.Options => ({
    chart: {
        events: {
            render: function (this: Highcharts.Chart) {
                horizontallyCenterTitle(this);
            } as ChartRenderCallbackFunction,
        },
        type: 'pie',
        backgroundColor: undefined,
        width: params.width,
        height: 525,
    },
    series: [
        {
            type: 'pie',
            data: params.seriesData,
            size: '475px',
            innerSize: '77.5%',
            states: {
                hover: {
                    opacity: 1,
                    brightness: 0,
                    halo: { opacity: 0.6, size: 15 },
                },
                inactive: { opacity: 1 }, // prevent sections from fading out on hover
            },
        },
    ],
    accessibility: { enabled: false },
    credits: { enabled: false },
    exporting: { enabled: false },
    title: {
        verticalAlign: 'middle',
        align: 'center',
        useHTML: true,
        text: `<div style="text-align: center;">
            <div style="color: var(--fg-color-default-normal);font-size: var(--type-heading-medium-font-size);line-height: var(--type-heading-medium-line-height);font-weight: var(--type-heading-medium-regular-font-weight);">
               ${params.titleOptions.title}
           </div>
           <div style="color: var(--fg-color-default-emphasis);font-size: var(--type-heading-large-font-size);line-height: var(--type-heading-large-line-height);font-weight: var(--type-heading-large-regular-font-weight);">
               ${params.titleOptions.currency}&nbsp;${formatNumberToCompactNotation(params.titleOptions.amount)}
           </div>
           </div>`,
    },
    tooltip: {
        distance: 50,
        padding: 10,
        backgroundColor: 'var(--bg-color-default-normal)',
        outside: true,
        borderWidth: 0,
        formatter: function (this) {
            // @ts-expect-error  Problems with missing Highcharts types (type of tooltip formatter parameters are different from actual)
            const customPointData = this.point.options as CustomPointOptions;

            return `
                <span style="color: var(--fg-color-default-emphasis);
                    font-size: var(--type-label-small-font-size);
                    line-height: var(--type-label-small-line-height);
                    margin-bottom: var(--padding-vertical-xxs);">
                    ${customPointData.name}: ${formatNumberToCompactNotation(customPointData.y ?? 0)}
                </span>
                <br/>
                <span style="color: var(--fg-color-default-normal);
                    font-size: var(--type-body-extra-small-font-size);
                    line-height: var(--type-body-extra-small-line-height);">
                    ${(customPointData.weight * 100).toFixed(0)}%
                </span>`;
        },
    },
    legend: {
        enabled: true,
        align: params.legend.legendPosition,
        verticalAlign: 'top',
        layout: 'vertical',
        rtl: params.legend.legendRtl,
        floating: params.legend.floatLegend,
        itemMarginTop: 8,
        width: params.legend.width,
        itemMarginBottom: 10,
        symbolHeight: 12,
        itemStyle: {
            color: 'var(--fg-color-default-normal)',
            fontSize: 'var(--type-body-large-font-size)',
            lineHeight: 'var(--type-body-large-line-height)',
            textOverflow: 'ellipsis',
        },
    },
    plotOptions: {
        pie: {
            borderColor: undefined,
            dataLabels: { enabled: false },
            showInLegend: true,
            point: {
                events: { legendItemClick: () => false },
            },
        },
    },
});
